
import { useEffect, useMemo, useRef, useState } from 'react';
import { ControlValues } from '../../../shared/types/spc.ts';
import { useResizeObserver } from '../../hooks/useResizeObserver.ts';
import { Batch, Operation } from '../../types/sharedTypeImpl.ts';
import { ModalDialog } from '../modalDialog.tsx';
import { prepareData } from './prepare-data/prepareData.ts';
import { renderGraph } from './render-graph/renderGraph.ts';
import { MeasurementGraphSettings } from './settings/measurementGraphSettings.ts';


const settingsDefault = { lineVisibilities: { nominal: true, tolerance: true, mean: true } };

export interface MGraphMeasurement {
    value: string | null;
    isHighlighted: boolean;
    date: Date;
    partCount: number | null;
    partIndex: number;
    hasMultipleParts: boolean;
    source: string;
    issues: string[];
}

interface Props {
    className?: string;
    style?: React.CSSProperties;
    measurements: MGraphMeasurement[] | null;
    controlValues: ControlValues | null;
    openDialogOnClick: boolean;
    batches?: Batch[] | null;
    operations?: Operation[] | null;
    settings?: MeasurementGraphSettings | null;
}

export function MeasurementsGraph({
    className,
    style,
    measurements,
    controlValues,
    openDialogOnClick,
    batches,
    operations,
    settings,
}: Props) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const mainContainerRef = useRef<HTMLDivElement>(null);
    const dialogContainerRef = useRef<HTMLDivElement>(null);

    const settingsCurrent = settings ?? settingsDefault;


    const preparedData = useMemo(() => {
        if (!measurements || !controlValues) return null;
        return prepareData(measurements, controlValues, settingsCurrent, batches, operations);
    }, [measurements, controlValues, settingsCurrent, batches, operations]);

    const mainDimensions = useResizeObserver(mainContainerRef);
    const dialogDimensions = useResizeObserver(dialogContainerRef);


    //Draw default graph
    useEffect(() => {
        if (!preparedData || !mainContainerRef.current) return undefined;
        renderGraph(
            mainContainerRef.current!,
            preparedData,
            true,
            settingsCurrent,
        );
    }, [preparedData, settingsCurrent, mainDimensions]);

    //Draw dialog graph
    useEffect(() => {
        if (!preparedData || !dialogContainerRef.current) return undefined;
        renderGraph(
            dialogContainerRef.current!,
            preparedData,
            false,
            settingsCurrent,
        );
    }, [isDialogOpen, preparedData, settingsCurrent, dialogDimensions]);


    return (
        <>
            <div
                ref={mainContainerRef}
                className={className}
                style={style}
                onClick={() => openDialogOnClick && setIsDialogOpen(true)}
            />

            <ModalDialog
                isOpen={isDialogOpen}
                cancelOnOutsideClick={true}
                onCancel={() => setIsDialogOpen(false)}
            >
                <div
                    style={{ width: '1200px', height: '400px' }}
                    ref={dialogContainerRef}
                    className={className}
                />
            </ModalDialog>
        </>
    );
}
